import {
  type AuthChallengeSession,
  AuthMode,
  type AuthInfo,
} from '~/types/interfaces'
import { consumePostLoginRedirect } from '~/composables/auth/postLoginRedirect'

// --- Types ---

export interface ModeParams {
  title: string
  verifyUsernameInput: boolean
  verifyPasswordInput: boolean
  verifyCodeInput: boolean
  verifyPasswordMatch: boolean
  forceOpenUsername?: boolean
}

interface AuthChallenge {
  challengeName: string
  session?: string
  username: string
}

interface AuthError {
  error: string
  message: string
}

interface AuthResponse {
  status: string
  auth?: AuthInfo
  error?: AuthError
  challenge?: AuthChallenge
}

interface SubmitParams {
  username: string
  password?: string
  session?: string
  code?: string
}

// --- Offline users ---

export const offlineUsers = [
  { id: '52905569-5c17-46ed-b848-6e4c75f021ce', name: 'はじめてユーザー' },
  { id: '7881c380-5051-7089-bb13-a53dc5860d7b', name: '全能ユーザー' },
  { id: '4e95ff5f-8096-4fa3-a238-7498caa5673e', name: 'Ｄｒ．Ｄ' },
  { id: 'c0f25172-c65a-484e-b1d0-45def9fed540', name: 'みならい賢者' },
].map(u => ({
  ...u,
  label: `${u.name} (${u.id.slice(0, 8)})`,
}))

// --- Password validation rules ---

export const passwordRules = [
  (v: string) => !!v || 'パスワードを入力してください',
  (v: string) => v.length >= 8 || '８文字以上必要です',
  (v: string) => /[0-9]/.test(v) || '数字を含めてください',
  (v: string) => /[a-z]/.test(v) || '小文字アルファベットを含めてください',
  (v: string) => /[A-Z]/.test(v) || '大文字アルファベットを含めてください',
  (v: string) =>
    /[\^$*.[\]{}()?\-"!@#%&,><':;|_~`+=]/.test(v) || '記号を含めてください',
]

// --- Mode params computation ---

export function computeModeParams(
  mode: AuthMode,
  authChallengeSession: Ref<AuthChallengeSession | undefined>,
  username: Ref<string>,
): ModeParams {
  switch (mode) {
    case AuthMode.SIGNUP:
      return {
        title: 'サインアップ',
        verifyUsernameInput: true,
        verifyPasswordInput: true,
        verifyCodeInput: false,
        verifyPasswordMatch: true,
      }
    case AuthMode.CONFIRM:
      return {
        title: 'コードを送信',
        verifyUsernameInput: false,
        verifyPasswordInput: false,
        verifyCodeInput: true,
        verifyPasswordMatch: false,
      }
    case AuthMode.RESEND:
      return {
        title: 'コードを送信',
        verifyUsernameInput: false,
        verifyPasswordInput: false,
        verifyCodeInput: false,
        verifyPasswordMatch: false,
      }
    case AuthMode.FORGOTPW:
      return {
        title: 'パスワードをリセット',
        verifyUsernameInput: true,
        verifyPasswordInput: false,
        verifyCodeInput: false,
        verifyPasswordMatch: false,
      }
    case AuthMode.RESETPW:
      return {
        title: 'パスワードをリセット',
        verifyUsernameInput: authChallengeSession.value?.username
          ? false
          : true,
        verifyPasswordInput: true,
        verifyCodeInput: false,
        verifyPasswordMatch: true,
        forceOpenUsername: authChallengeSession.value?.username
          ? undefined
          : true,
      }
    case AuthMode.SETNEWPW:
      if (authChallengeSession.value?.username) {
        username.value = authChallengeSession.value?.username
      }
      return {
        title: '新しいパスワードを設定',
        verifyUsernameInput: false,
        verifyPasswordInput: true,
        verifyCodeInput: false,
        verifyPasswordMatch: true,
      }
    case AuthMode.OFFLINE:
      return {
        title: 'サインイン（オフライン）',
        verifyUsernameInput: false,
        verifyPasswordInput: false,
        verifyCodeInput: false,
        verifyPasswordMatch: false,
      }
    case AuthMode.SIGNIN:
    default:
      return {
        title: 'サインイン',
        verifyUsernameInput: true,
        verifyPasswordInput: true,
        verifyCodeInput: false,
        verifyPasswordMatch: false,
      }
  }
}

// --- Cognito error mapping ---

function mapCognitoError(errorCode: string): string {
  switch (errorCode) {
    case 'InvalidPasswordException':
      return 'パスワードは数字、大文字、小文字、記号を含む８文字以上としてください'
    case 'UserNotFoundException':
      return '指定したユーザーは登録されていません'
    case 'NotAuthorizedException':
      return 'ユーザー名またはパスワードが間違っています'
    case 'UsernameExistsException':
      return 'すでに存在するユーザーです'
    default:
      return errorCode
  }
}

// --- Submit (API call + challenge handling) ---

async function submitAuth(
  mode: AuthMode,
  params: SubmitParams,
  authChallengeSession: Ref<AuthChallengeSession | undefined>,
): Promise<string> {
  console.log(`Submit Auth: `, mode, params)

  const config = useRuntimeConfig()
  const resp = await $fetch<AuthResponse>(
    `${config.public.apiBase}/api/v1/auth`,
    {
      method: 'POST',
      query: { challenge: mode },
      headers: { 'Content-Type': 'application/json' },
      body: params,
    },
  )
  console.log(typeof resp, resp, resp.status)

  if (resp.status === 'error') {
    throw new Error(mapCognitoError(resp.error!.error))
  }

  if (resp.status === 'challenge') {
    const challenge = resp.challenge!
    switch (challenge.challengeName) {
      case 'NEW_PASSWORD_REQUIRED': {
        console.log(challenge.challengeName)
        authChallengeSession.value = {
          challengeName: challenge.challengeName,
          session: challenge.session,
          username: challenge.username,
        }
        return '/setnewpw'
      }
      case 'CODE_CONFIRMATION_REQUIRED': {
        console.log(challenge.challengeName)
        authChallengeSession.value = {
          challengeName: challenge.challengeName,
          username: challenge.username,
          password: authChallengeSession.value?.password,
        }
        return '/confirm'
      }
      case 'RESET_PASSWORD_REQUIRED': {
        console.log(challenge.challengeName)
        authChallengeSession.value = {
          challengeName: challenge.challengeName,
          username: challenge.username,
        }
        return '/resetpw'
      }
      case 'SIGNIN_REQUIRED': {
        console.log(challenge.challengeName)
        if (
          authChallengeSession.value?.username
          && authChallengeSession.value?.password
        ) {
          return submitAuth(AuthMode.SIGNIN, {
            username: authChallengeSession.value.username,
            password: authChallengeSession.value.password,
          }, authChallengeSession)
        }
        return '/signin'
      }
    }
  }

  authChallengeSession.value = undefined

  useAuthInfo().setAuthInfo(resp.auth!)
  useGameStateStore().reset()
  useAuthStateStore().reset()

  return '/adventurersCamp'
}

// --- Build submit parameters ---

function buildSubmitParams(
  mode: AuthMode,
  username: string,
  password: string,
  code: string,
  authChallengeSession: Ref<AuthChallengeSession | undefined>,
): SubmitParams {
  switch (mode) {
    case AuthMode.SIGNUP:
      authChallengeSession.value = {
        challengeName: 'SIGNUP',
        username,
        password,
      }
      return { username, password }
    case AuthMode.SETNEWPW:
      if (authChallengeSession.value) {
        const params: SubmitParams = {
          session: authChallengeSession.value.session,
          username: authChallengeSession.value.username!,
          password,
        }
        authChallengeSession.value = undefined
        return params
      }
      authChallengeSession.value = undefined
      return { username: '' }
    case AuthMode.CONFIRM:
      if (authChallengeSession.value) {
        return {
          username: authChallengeSession.value.username!,
          code,
        }
      }
      return { username: '' }
    case AuthMode.FORGOTPW:
      return { username }
    case AuthMode.RESETPW: {
      const params: SubmitParams = {
        username: authChallengeSession.value?.username || username,
        session: authChallengeSession.value?.session,
        password,
        code,
      }
      authChallengeSession.value = undefined
      return params
    }
    case AuthMode.RESEND:
      return { username }
    case AuthMode.OFFLINE:
      return { username }
    case AuthMode.SIGNIN:
    default:
      return { username, password }
  }
}

// --- Composable ---

export function useAuthFlow(mode: Ref<AuthMode>) {
  const { trackSignUp } = useFunnelEvent()
  const authChallengeSession = useState<AuthChallengeSession | undefined>(
    'authChallengeSession',
  )

  const username = ref<string>('')
  const password = ref<string>('')
  const passwordConfirm = ref<string>('')
  const code = ref<string>('')
  const agreed = ref<boolean>(false)
  const visible = ref<boolean>(false)
  const loading = ref<boolean>(false)

  const modeParams = ref<ModeParams>(
    computeModeParams(mode.value, authChallengeSession, username),
  )

  const isOfflineAvailable = computed(() => {
    return useRuntimeConfig().public.environment === 'local'
  })

  const onSubmit = async () => {
    console.log(`Auth: ${mode.value}`, modeParams.value)

    // Check submit conditions
    if (modeParams.value.verifyUsernameInput && !username.value) {
      alert('メールアドレスを入力してください')
      return
    }
    if (mode.value === AuthMode.OFFLINE && !username.value) {
      alert('ユーザーを選択してください')
      return
    }
    if (modeParams.value.verifyPasswordInput && !password.value) {
      alert('パスワードを入力してください')
      return
    }
    if (modeParams.value.verifyCodeInput && !code.value) {
      alert('検証コードを入力してください')
      return
    }
    if (
      modeParams.value.verifyPasswordMatch
      && password.value !== passwordConfirm.value
    ) {
      alert('パスワードが一致しません')
      return
    }

    // Try to submit
    loading.value = true
    try {
      const parameters = buildSubmitParams(
        mode.value,
        username.value,
        password.value,
        code.value,
        authChallengeSession,
      )
      const pageTo = await submitAuth(mode.value, parameters, authChallengeSession)
      if (mode.value === AuthMode.CONFIRM && pageTo === '/adventurersCamp') {
        trackSignUp('email')
      }
      // 認証必須ページ（例: /connect の OAuth 同意）から飛ばされてきた場合は、元の URL へ戻す。
      const back = consumePostLoginRedirect()
      if (back) return navigateTo(back)
      return navigateTo(pageTo)
    }
    catch (e) {
      const error = e as Error
      console.error(error.message)
      loading.value = false

      alert(`${modeParams.value.title}に失敗しました：${error.message}`)

      return navigateTo(`/${mode.value}`)
    }
  }

  return {
    // State
    username,
    password,
    passwordConfirm,
    code,
    agreed,
    visible,
    loading,

    // Computed / derived
    modeParams,
    isOfflineAvailable,
    authChallengeSession,

    // Actions
    onSubmit,
  }
}
